// Centralized error handler for HTTP and service configuration errors
class ServiceErrorHandler {
  constructor() {
    this.errorCategories = {
      CONFIGURATION: 'configuration',
      NETWORK: 'network', 
      SERVICE: 'service',
      AUTHENTICATION: 'authentication',
      VALIDATION: 'validation'
    };

    this.errorCodes = {
      TWILIO_CONFIG_ERROR: 'TWILIO_CONFIG_ERROR',
      RAZORPAY_CONFIG_ERROR: 'RAZORPAY_CONFIG_ERROR',
      SUPABASE_CONFIG_ERROR: 'SUPABASE_CONFIG_ERROR',
      SUPABASE_PAUSED: 'SUPABASE_PAUSED',
      NETWORK_ERROR: 'NETWORK_ERROR',
      SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
      NON_2XX_STATUS: 'NON_2XX_STATUS'
    };
  }

  /**
   * Parse and categorize HTTP response errors
   * @param {Error} error - The error object
   * @param {string} serviceName - Name of the service that generated the error
   * @returns {Object} Structured error response
   */
  parseServiceError(error, serviceName = 'unknown') {
    console.error(`ServiceErrorHandler: Processing error for ${serviceName}:`, {
      message: error?.message,
      stack: error?.stack,
      name: error?.name
    });

    // Handle non-2xx status errors (primary focus)
    if (this._isNon2xxError(error)) {
      return this._handleNon2xxError(error, serviceName);
    }

    // Handle network connectivity errors
    if (this._isNetworkError(error)) {
      return this._handleNetworkError(error, serviceName);
    }

    // Handle authentication errors
    if (this._isAuthError(error)) {
      return this._handleAuthError(error, serviceName);
    }

    // Handle timeout errors
    if (this._isTimeoutError(error)) {
      return this._handleTimeoutError(error, serviceName);
    }

    // Default fallback error
    return this._handleUnknownError(error, serviceName);
  }

  /**
   * Check if error is a non-2xx HTTP status error
   * @private
   */
  _isNon2xxError(error) {
    return (
      // Match 4xx or 5xx status codes and new FunctionsHttpError
      (error?.message?.includes('non-2xx status') ||
      error?.message?.includes('Non-2xx status detected') ||
      error?.message?.includes('FunctionsHttpError') ||
      error?.message?.includes('Edge Function returned a non-2xx status code') ||
      error?.name === 'FunctionsHttpError' ||
      error?.name === 'FunctionsFetchError'|| error?.message?.includes('Failed to send a request to the Edge Function') || /[45]\d{2}/?.test(error?.message))
    );
  }

  /**
   * Handle non-2xx status errors with service-specific guidance
   * @private
   */
  _handleNon2xxError(error, serviceName) {
    const errorMessage = error?.message || '';
    let category = this.errorCategories?.CONFIGURATION;
    let code = this.errorCodes?.NON_2XX_STATUS;
    let userMessage = 'Service configuration error detected';
    let technicalDetails = errorMessage;
    let configurationHelp = null;
    let actionRequired = true;

    if (errorMessage?.toLowerCase()?.includes('paused')) {
      return {
        success: false,
        error: '⚠️ Your Supabase project is paused. Resume it in the Supabase dashboard to send OTPs.',
        code: this.errorCodes?.SUPABASE_PAUSED,
        category: this.errorCategories?.SERVICE,
        serviceName,
        technicalDetails: errorMessage,
        actionRequired: true,
        retryable: true,
        timestamp: new Date()?.toISOString()
      };
    }

    // Handle FunctionsHttpError specifically
    if (error?.name === 'FunctionsHttpError' || errorMessage?.includes('Edge Function returned a non-2xx status code')) {
      userMessage = '⚠️ Edge Function returned an error status. This typically means Twilio credentials are missing or invalid.';
      code = 'EDGE_FUNCTION_HTTP_ERROR';
      technicalDetails = 'The edge function is accessible but returned an error response, indicating configuration issues with Twilio credentials.';
      
      if (serviceName?.toLowerCase()?.includes('otp') || serviceName?.toLowerCase()?.includes('twilio')) {
        configurationHelp = this._getTwilioConfigHelp();
        configurationHelp?.instructions?.unshift('0. The edge function is working but needs proper Twilio configuration');
      }
    }

    // Handle FunctionsFetchError specifically
    if (error?.name === 'FunctionsFetchError' || errorMessage?.includes('Failed to send a request to the Edge Function')) {
      userMessage = '⚠️ Edge Function error. This usually means the service is not configured or deployed properly.';
      code = 'EDGE_FUNCTION_FETCH_ERROR';
      technicalDetails = 'Could not connect to the edge function. This typically indicates missing environment variables or deployment issues.';
      
      if (serviceName?.toLowerCase()?.includes('otp') || serviceName?.toLowerCase()?.includes('twilio')) {
        configurationHelp = this._getTwilioConfigHelp();
        configurationHelp?.instructions?.unshift('0. Verify the edge function is deployed in your Supabase project');
      }
    }

    // Detect specific HTTP status codes
    const statusCode = this._extractStatusCode(errorMessage);
    
    switch (statusCode) {
      case 422:
        userMessage = '⚠️ Service configuration required. Click "Setup Guide" for instructions.';
        code = this.errorCodes?.TWILIO_CONFIG_ERROR;
        configurationHelp = this._getTwilioConfigHelp();
        break;
        
      case 500:
        userMessage = 'Internal server error. This may be due to missing configuration.';
        technicalDetails = 'Server encountered an error, typically due to missing credentials.';
        break;
        
      case 503:
        userMessage = 'Service temporarily unavailable. Please try again in a moment.';
        category = this.errorCategories?.SERVICE;
        actionRequired = false;
        break;
        
      case 404:
        userMessage = 'Service endpoint not found. The function may not be deployed properly.';
        technicalDetails = 'Edge function may not be deployed to your Supabase project.';
        break;
        
      case 401:
      case 403:
        userMessage = 'Authentication failed. Please check service credentials.';
        category = this.errorCategories?.AUTHENTICATION;
        break;
        
      default:
        // Service-specific handling based on service name
        if (serviceName?.toLowerCase()?.includes('twilio') || serviceName?.toLowerCase()?.includes('otp')) {
          userMessage = '⚠️ SMS service configuration error. Click "Setup Guide" below for help.';
          code = this.errorCodes?.TWILIO_CONFIG_ERROR;
          configurationHelp = this._getTwilioConfigHelp();
        } else if (serviceName?.toLowerCase()?.includes('razorpay') || serviceName?.toLowerCase()?.includes('payment')) {
          userMessage = '⚠️ Payment service configuration error. Please check Razorpay credentials.';
          code = this.errorCodes?.RAZORPAY_CONFIG_ERROR;
          configurationHelp = this._getRazorpayConfigHelp();
        }
    }

    return {
      success: false,
      error: userMessage,
      code,
      category,
      serviceName,
      statusCode,
      technicalDetails,
      configurationHelp,
      actionRequired,
      requiresConfiguration: actionRequired && category === this.errorCategories?.CONFIGURATION,
      timestamp: new Date()?.toISOString(),
      retryable: !actionRequired || category === this.errorCategories?.SERVICE
    };
  }

  /**
   * Extract HTTP status code from error message
   * @private
   */
  _extractStatusCode(errorMessage) {
    const statusMatch = errorMessage?.match(/(\d{3})/);
    return statusMatch ? parseInt(statusMatch?.[1]) : null;
  }

  /**
   * Check if error is network-related
   * @private
   */
  _isNetworkError(error) {
    const errorMessage = error?.message?.toLowerCase() || '';
    return (errorMessage?.includes('failed to fetch') ||
    errorMessage?.includes('networkerror') ||
    errorMessage?.includes('network request failed') ||
    errorMessage?.includes('connection refused') || errorMessage?.includes('connection timeout'));
  }

  /**
   * Handle network connectivity errors
   * @private
   */
  _handleNetworkError(error, serviceName) {
    return {
      success: false,
      error: 'Network connection issue. Please check your internet connection and try again.',
      code: this.errorCodes?.NETWORK_ERROR,
      category: this.errorCategories?.NETWORK,
      serviceName,
      technicalDetails: error?.message,
      actionRequired: false,
      retryable: true,
      timestamp: new Date()?.toISOString(),
      userAction: 'Check your internet connection and try again in a moment.'
    };
  }

  /**
   * Check if error is authentication-related
   * @private
   */
  _isAuthError(error) {
    const errorMessage = error?.message?.toLowerCase() || '';
    return (errorMessage?.includes('unauthorized') ||
    errorMessage?.includes('authentication') ||
    errorMessage?.includes('invalid token') || errorMessage?.includes('access denied'));
  }

  /**
   * Handle authentication errors
   * @private
   */
  _handleAuthError(error, serviceName) {
    return {
      success: false,
      error: 'Authentication failed. Please check your credentials and try again.',
      code: this.errorCodes?.AUTHENTICATION,
      category: this.errorCategories?.AUTHENTICATION,
      serviceName,
      technicalDetails: error?.message,
      actionRequired: true,
      requiresConfiguration: true,
      timestamp: new Date()?.toISOString()
    };
  }

  /**
   * Check if error is timeout-related
   * @private
   */
  _isTimeoutError(error) {
    const errorMessage = error?.message?.toLowerCase() || '';
    return (errorMessage?.includes('timeout') || errorMessage?.includes('request took too long'));
  }

  /**
   * Handle timeout errors
   * @private
   */
  _handleTimeoutError(error, serviceName) {
    return {
      success: false,
      error: 'Request timed out. Please try again.',
      code: 'TIMEOUT_ERROR',
      category: this.errorCategories?.SERVICE,
      serviceName,
      technicalDetails: error?.message,
      actionRequired: false,
      retryable: true,
      timestamp: new Date()?.toISOString()
    };
  }

  /**
   * Handle unknown/unclassified errors
   * @private
   */
  _handleUnknownError(error, serviceName) {
    return {
      success: false,
      error: 'An unexpected error occurred. Please try again or contact support if the issue persists.',
      code: 'UNKNOWN_ERROR',
      category: this.errorCategories?.SERVICE,
      serviceName,
      technicalDetails: error?.message || 'Unknown error',
      actionRequired: false,
      retryable: true,
      timestamp: new Date()?.toISOString(),
      suggestion: 'If the problem persists, please contact technical support.'
    };
  }

  /**
   * Get Twilio configuration help information
   * @private
   */
  _getTwilioConfigHelp() {
    return {
      title: 'Twilio SMS Configuration Required',
      message: 'To enable OTP functionality, please configure Twilio credentials:',
      variables: [
        'TWILIO_ACCOUNT_SID - Your Twilio Account SID',
        'TWILIO_AUTH_TOKEN - Your Twilio Auth Token',
        'TWILIO_WHATSAPP_FROM - Your Twilio WhatsApp number (e.g., whatsapp:+14155238886)'
      ],
      instructions: [
        '1. Sign up for Twilio account at https://www.twilio.com',
        '2. Get credentials from Twilio Console Dashboard',
        '3. Set up WhatsApp Sandbox for testing',
        '4. Join sandbox by sending "join <sandbox-name>" to Twilio number',
        '5. Go to Supabase Dashboard → Edge Functions → Settings',
        '6. Add the environment variables listed above',
        '7. Redeploy your edge functions',
        '8. Test OTP functionality'
      ],
      links: {
        twilioConsole: 'https://console.twilio.com',
        whatsappSandbox: 'https://console.twilio.com/us1/develop/sms/try-it-out/whatsapp-learn'
      }
    };
  }

  /**
   * Get Razorpay configuration help information
   * @private
   */
  _getRazorpayConfigHelp() {
    return {
      title: 'Razorpay Payment Configuration Required',
      message: 'To enable payment functionality, please configure Razorpay credentials:',
      variables: [
        'RAZORPAY_KEY_ID - Your Razorpay Key ID',
        'RAZORPAY_KEY_SECRET - Your Razorpay Key Secret'
      ],
      instructions: [
        '1. Sign up for Razorpay account at https://razorpay.com',
        '2. Complete KYC verification process',
        '3. Get API keys from Razorpay Dashboard → Settings → API Keys',
        '4. Go to Supabase Dashboard → Edge Functions → Settings',
        '5. Add the environment variables listed above',
        '6. Redeploy your edge functions',
        '7. Test payment functionality'
      ],
      links: {
        razorpayDashboard: 'https://dashboard.razorpay.com',
        apiKeys: 'https://dashboard.razorpay.com/app/keys'
      }
    };
  }

  /**
   * Format error for display in UI components
   * @param {Object} errorResult - Result from parseServiceError
   * @returns {Object} UI-formatted error
   */
  formatForUI(errorResult) {
    return {
      message: errorResult?.error,
      type: errorResult?.category === this.errorCategories?.CONFIGURATION ? 'warning' : 'error',
      title: this._getErrorTitle(errorResult),
      details: errorResult?.technicalDetails,
      actions: this._getErrorActions(errorResult),
      dismissible: !errorResult?.actionRequired,
      persistent: errorResult?.actionRequired,
      showConfigGuide: errorResult?.requiresConfiguration,
      configurationHelp: errorResult?.configurationHelp
    };
  }

  /**
   * Get appropriate error title based on error type
   * @private
   */
  _getErrorTitle(errorResult) {
    switch (errorResult?.category) {
      case this.errorCategories?.CONFIGURATION:
        return 'Configuration Required';
      case this.errorCategories?.NETWORK:
        return 'Connection Issue';
      case this.errorCategories?.AUTHENTICATION:
        return 'Authentication Error';
      case this.errorCategories?.SERVICE:
        return 'Service Unavailable';
      default:
        return 'Error';
    }
  }

  /**
   * Get suggested actions based on error type
   * @private
   */
  _getErrorActions(errorResult) {
    const actions = [];

    if (errorResult?.retryable) {
      actions?.push({
        label: 'Try Again',
        action: 'retry',
        variant: 'default'
      });
    }

    if (errorResult?.requiresConfiguration) {
      actions?.push({
        label: 'Setup Guide',
        action: 'showConfig',
        variant: 'outline'
      });
    }

    if (errorResult?.category === this.errorCategories?.NETWORK) {
      actions?.push({
        label: 'Check Connection',
        action: 'checkConnection',
        variant: 'outline'
      });
    }

    return actions;
  }

  /**
   * Log error for debugging and monitoring
   * @param {Object} errorResult - Result from parseServiceError
   */
  logError(errorResult) {
    const logLevel = errorResult?.actionRequired ? 'error' : 'warn';
    
    console.group(`🚨 ServiceError [${errorResult?.code}]`);
    console[logLevel]('Service:', errorResult?.serviceName);
    console[logLevel]('Category:', errorResult?.category);
    console[logLevel]('User Message:', errorResult?.error);
    console[logLevel]('Technical Details:', errorResult?.technicalDetails);
    if (errorResult?.statusCode) {
      console[logLevel]('HTTP Status:', errorResult?.statusCode);
    }
    console[logLevel]('Requires Action:', errorResult?.actionRequired);
    console[logLevel]('Timestamp:', errorResult?.timestamp);
    console.groupEnd();

    // Log configuration help if available
    if (errorResult?.configurationHelp) {
      console.group('🔧 Configuration Help');
      console.info(errorResult?.configurationHelp?.message);
      errorResult?.configurationHelp?.variables?.forEach(variable => {
        console.info(`• ${variable}`);
      });
      console.groupEnd();
    }
  }
}

// Create singleton instance
const serviceErrorHandler = new ServiceErrorHandler();

export default serviceErrorHandler;
export { ServiceErrorHandler };
